import React, { useEffect, useState } from "react";
import { BringzzText, BringzzIcon, BringzzButton } from "@bringzz/components";
import { useNavigation } from "../../context/NavigationContext";
import { useAuth } from "../../context/AuthContext";
import useRequest from "hooks/useRequest";

const AddressSelect = ({ close, setAddress, className, ...props }) => {
  const { navigate } = useNavigation();
  const { user } = useAuth();
  const { sendRequest } = useRequest();
  const [addresses, setAddresses] = useState([]);
  const [activeAddress, setActiveAddress] = useState(null); 
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('home');
  const [locationError, setLocationError] = useState(null);

  const handleUseCurrentLocation = async () => {
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);
    
    try {
      const position = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Location request timed out. Please try again.'));
        }, 15000);
        
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeoutId);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timeoutId);
            reject(err);
          },
          {
            enableHighAccuracy: true,
            timeout: 30000,
            maximumAge: 0
          }
        );
      });

      if (!window.mapkit) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      if (!window.mapkit.isInitialized) {
        window.mapkit.init({
          authorizationCallback: (done) => {
            done(process.env.REACT_APP_APPLE_MAP_KEY);
          },
        });
      }

      const geocoder = new window.mapkit.Geocoder();
      const coordinate = new window.mapkit.Coordinate(position.coords.latitude, position.coords.longitude);
      
      const geocodeResult = await new Promise((resolve, reject) => {
        geocoder.reverseLookup(coordinate, (error, data) => {
          if (error) {
            console.error('MapKit geocoding error:', error);
            reject(error);
          } else {
            resolve(data.results[0]);
          }
        });
      });

      let postalCode = geocodeResult.postalCode || '';
      let streetName = geocodeResult.thoroughfare || '';
      let houseNumber = geocodeResult.subThoroughfare || '';
      let cityName = geocodeResult.locality || geocodeResult.administrativeArea || '';
      let countryName = geocodeResult.country || '';

      if (!postalCode) {
        try {
          const nominatimResponse = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}&addressdetails=1`
          );
          const nominatimData = await nominatimResponse.json();
          
          if (nominatimData.address) {
            postalCode = nominatimData.address.postcode || '';
            if (!streetName) streetName = nominatimData.address.road || '';
          }
        } catch (nominatimError) {
          console.warn('Nominatim geocoding failed:', nominatimError);
        }
      }
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      
      if (!lat || !lng || isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        throw new Error('Invalid GPS coordinates received');
      }

      const response = await sendRequest({
        method: "POST",
        url: "/module/address/setLocationAddress",
        data: {
          latitude: lat,
          longitude: lng,
          streetName,
          houseNumber,
          cityName,
          postalCode,
          countryName,
        }
      });

      if (response.status === "success") {
        if (props.onAddressUpdated) {
          props.onAddressUpdated();
        }
        await fetchAddresses();
        close();
      }
    } catch (error) {
      console.error("Location error:", error);
      let errorMessage = "Could not detect your location";
      
      if (error.code === 1) {
        errorMessage = "Location access denied. Please enable location permissions.";
      } else if (error.code === 2) {
        errorMessage = "Location unavailable. Please try again.";
      } else if (error.code === 3 || error.message?.includes('timeout')) {
        errorMessage = "Location request timed out. Please try again.";
      }
      
      setLocationError(errorMessage);
    } finally {
      setIsLocating(false);
    }
  };

  const syncActiveAddressToStorage = (address) => {
    if (address) {
      localStorage.setItem('activeAddress', JSON.stringify(address));
    } else {
      localStorage.removeItem('activeAddress');
    }
  };

  const fetchActiveAddress = async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: "/module/address/getActiveAddress",
      });
      if (response.status === "success") {
        setActiveAddress(response.data);
        syncActiveAddressToStorage(response.data);
        if (props.onActiveAddressFetched) props.onActiveAddressFetched(response.data);
      } else {
        setActiveAddress(null);
        syncActiveAddressToStorage(null);
        console.error("Failed to fetch the active address");
      }
    } catch (err) {
      setActiveAddress(null);
      syncActiveAddressToStorage(null);
      console.error("Error fetching active address:", err);
    }
  };

  const fetchAddresses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await sendRequest({
        method: "GET",
        url: "/module/address/getAddress",
      });
      if (response.status === "success" && Array.isArray(response.data)) {
        setAddresses(response.data);
        
        if (user?.userRole === 1 && response.data.length === 1 && !response.data[0].isActive) {
          await setActiveAddressHandler(response.data[0].integrationId);
        }
      } else {
        setError("Failed to load addresses.");
      }
    } catch (err) {
      console.error("Failed to fetch addresses", err);
      setError("An error occurred while fetching addresses.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const stored = localStorage.getItem('activeAddress');
    if (stored) {
      try {
        setActiveAddress(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse activeAddress from localStorage:', e)
      }
    }
    if (user?.userRole !== 1) {
      fetchActiveAddress();
    }
    fetchAddresses();
  }, []);
  const formatAddress = (address) => {
    const streetPart = [address.streetName, address.houseNumber].filter(Boolean).join(' ');
    const cityPart = [address.postalCode, address.cityName].filter(Boolean).join(' ');
    return [streetPart, cityPart].filter(Boolean).join(', ');
  };

  const setActiveAddressHandler = async (integrationId) => {
    try {
      const response = await sendRequest({
        method: "POST",
        url: "/module/address/setActiveAddress",
        data: { integrationId },
      });
      if (response.status === "success") {
        if (user?.userRole !== 1) {
          await fetchActiveAddress();
        }
        await fetchAddresses();
        if (props.onAddressUpdated) props.onAddressUpdated();
      } else {
        console.error("Failed to set active address");
      }
    } catch (err) {
      console.error("Error setting active address:", err);
    }
  };

  return (
    <div className={`py-6 ${className}`}>
      <div className="flex flex-col px-4 gap-y-4 mt-4">
        {isLoading && (
          <div className="flex justify-center">
            <BringzzText tag="p">Loading addresses...</BringzzText>
          </div>
        )}

        {error && (
          <div className="flex justify-center">
            <BringzzText tag="p" className="text-red-500">
              {error}
            </BringzzText>
          </div>
        )}

        {!isLoading &&
          addresses.map((address) => {
            const canClick = !(user?.userRole === 1 && address.isOwner === false);
            return (
              <div
                key={address.id}
                className={`flex items-center justify-start gap-x-4 rounded-md p-4 min-h-[75px] ${
                  address.isActive 
                    ? 'border-2 border-purple-500 bg-purple-50' 
                    : canClick 
                      ? 'border hover:cursor-pointer hover:bg-gray-50'
                      : 'border opacity-60 cursor-not-allowed'
                }`}
                onClick={async () => {
                  if (!address.isActive && canClick) {
                    setAddress(formatAddress(address));
                    await setActiveAddressHandler(address.integrationId);
                  }
                }}
                role="button"
                tabIndex={canClick ? 0 : -1}
                aria-label={`${address.isActive ? 'Active address' : canClick ? 'Select address' : 'Address (read-only)'} ${address.streetName}, ${address.cityName}`}
                onKeyDown={async (e) => {
                  if (e.key === "Enter" && !address.isActive && canClick) {
                    setAddress(formatAddress(address));
                    await setActiveAddressHandler(address.integrationId);
                  }
                }}
              >
                <BringzzIcon 
                  folder="LineIcons" 
                  icon={address.isActive ? "IconHome" : "IconBriefcase"} 
                  size="20" 
                  className={address.isActive ? "text-purple-600" : ""}
                />
                <div className="flex flex-col">
                  <BringzzText tag="h4" className="font-bold">
                    {address.addressLable || "Address"}
                  </BringzzText>
                  <BringzzText
                    tag="h4"
                    className="font-regular text-midnight-blue/80"
                  >
                    {formatAddress(address)}
                  </BringzzText>
                </div>
              </div>
            );
          })}
        {/* Use current location - Hide for pharmacy users with addresses */}
        {!(user?.userRole === 1 && addresses.length > 0) && (
          <div className="space-y-4">
            <div
              className={`flex items-center justify-between gap-x-4 border rounded-md p-4 min-h-[75px] hover:cursor-pointer ${
                isLocating ? 'opacity-75 pointer-events-none' : 'hover:bg-gray-50'
              }`}
              onClick={handleUseCurrentLocation}
              role="button"
            >
              <div className="flex items-center gap-4">
                <BringzzIcon 
                  folder="LineIcons" 
                  icon="IconLocation" 
                  size="20" 
                  className={isLocating ? "animate-spin text-purple-500" : "text-gray-600"}
                />
                <div className="flex flex-col">
                  <BringzzText tag="h4" className="font-bold">
                    {isLocating ? "Detecting your location..." : "Use Current Location"}
                  </BringzzText>
                  {!isLocating && (
                    <BringzzText tag="p" className="text-midnight-blue/80">
                      We will use your device&apos;s location
                    </BringzzText>
                  )}
                </div>
              </div>
            </div>
            {locationError && (
              <div className="flex justify-center pt-2">
                <BringzzText tag="p" className="text-red-500 text-sm">
                  {locationError}
                </BringzzText>
              </div>
            )}
          </div>
        )}

        {/* Divider - Hide for pharmacy users with addresses or non-owners */}
        {!(user?.userRole === 1 && addresses.length > 0) && 
         !(user?.userRole === 1 && addresses.some(addr => addr.isOwner === false)) && (
          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-white text-gray-500 text-sm">OR</span>
            </div>
          </div>
        )}

        {!(user?.userRole === 1 && addresses.length > 0) && 
         !(user?.userRole === 1 && addresses.some(addr => addr.isOwner === false)) && (
          <div
            className="flex items-center justify-start gap-x-4 border rounded-md p-4 min-h-[75px] hover:cursor-pointer"
            onClick={() => navigate("/settings/account/address?new=true")}
            role="button"
            tabIndex={0}
            aria-label="Add new address"
            onKeyDown={(e) => e.key === "Enter" && navigate("/settings/account/address?new=true")}
          >
            <BringzzIcon folder="LineIcons" icon="IconPlus" size="20" />
            <div className="flex flex-col">
              <BringzzText tag="h4" className="font-bold">
                Add new address
              </BringzzText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressSelect;
